import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { ShieldAlert, ShieldCheck, Lock } from 'lucide-react'
import { recordAccessService } from '@/services/recordAccess.service'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

interface RecordAccessBannerProps {
  patientId: string
  /** Clinical content gated behind a treating relationship / break-the-glass. */
  children?: React.ReactNode
}

/**
 * NOM-004 REQ-AUTH-02 / REQ-AUTH-04.
 * Shows the accessor's relationship to the patient and warns that access is
 * logged. When the user has no treating relationship, clinical content is
 * gated behind a mandatory "break-the-glass" justification that is written to
 * the immutable audit log.
 */
export function RecordAccessBanner({ patientId, children }: RecordAccessBannerProps) {
  const [granted, setGranted] = useState(false)
  const [open, setOpen] = useState(false)
  const [justification, setJustification] = useState('')

  const { data: relationship } = useQuery({
    queryKey: ['record-relationship', patientId],
    queryFn: () => recordAccessService.getRelationship(patientId),
    enabled: !!patientId,
  })

  const breakGlass = useMutation({
    mutationFn: () => recordAccessService.breakTheGlass(patientId, justification),
    onSuccess: () => {
      setGranted(true)
      setOpen(false)
    },
  })

  if (!relationship) return null

  const needsBreakGlass = relationship.requires_break_glass && !granted

  return (
    <>
      <div
        className={`px-6 lg:px-12 py-2 text-xs flex items-center gap-2 border-b ${
          needsBreakGlass
            ? 'bg-amber-50 border-amber-200 text-amber-900'
            : 'bg-ml-surface-container/40 border-ml-outline-variant/10 text-ml-on-surface-variant'
        }`}
      >
        {needsBreakGlass ? (
          <ShieldAlert className="h-4 w-4 flex-shrink-0 text-amber-600" />
        ) : (
          <ShieldCheck className="h-4 w-4 flex-shrink-0 text-teal-600" />
        )}
        <span className="font-medium">{relationship.relationship}.</span>
        <span>Todo acceso a este expediente queda registrado en la bitácora.</span>
        {needsBreakGlass && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7 border-amber-300 text-amber-900 hover:bg-amber-100"
            onClick={() => setOpen(true)}
          >
            <Lock className="mr-1.5 h-3.5 w-3.5" />
            Registrar acceso de emergencia
          </Button>
        )}
      </div>

      {/* Clinical content is hidden until access is justified (REQ-AUTH-04 / NO-05) */}
      {!needsBreakGlass && children}

      {/* Blocking overlay until justification is captured (REQ-AUTH-04 / NO-05) */}
      {needsBreakGlass && (
        <div className="px-6 lg:px-12 py-8">
          <div className="max-w-2xl mx-auto rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
            <ShieldAlert className="mx-auto h-8 w-8 text-amber-600" />
            <h3 className="mt-3 text-base font-semibold text-amber-900">
              Acceso fuera de la relación de tratamiento
            </h3>
            <p className="mt-1 text-sm text-amber-800">
              No existe una relación de tratamiento registrada entre usted y este
              paciente. Para consultar el contenido clínico debe justificar el acceso;
              el evento se registrará en la bitácora de accesos.
            </p>
            <Button
              className="mt-4 bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => setOpen(true)}
            >
              <Lock className="mr-2 h-4 w-4" />
              Justificar y continuar
            </Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Romper el cristal — justificación requerida</DialogTitle>
            <DialogDescription>
              Describa el motivo del acceso (emergencia, cobertura de guardia, etc.).
              Mínimo 10 caracteres. Este registro es permanente.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Motivo del acceso de emergencia…"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              disabled={justification.trim().length < 10 || breakGlass.isPending}
              onClick={() => breakGlass.mutate()}
            >
              Registrar acceso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
